import { useCallback, useEffect, useRef, useState } from 'react'
import QRCode from 'qrcode'
import { Icon } from '@iconify/react'
import { ApiClientError } from '@/lib/api/client'
import { MascotCoin } from './MascotCoin'
import {
  createMascotCoinPurchase,
  fetchMascotCoinCatalog,
  fetchMascotCoinPurchase,
  type MascotCoinCatalog,
  type MascotCoinPurchase,
} from './mascot-coins-api'
import styles from './MascotGame.module.css'
import coinStyles from './MascotCoinShop.module.css'

const formatPrice = (cents: number) =>
  (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const message = (error: unknown) =>
  error instanceof ApiClientError
    ? Object.values(error.errors ?? {}).flat()[0] || error.message
    : 'Não foi possível consultar a compra. Tente novamente.'

export function MascotCoinShop({
  campaignId,
  disabled,
  onPaid,
}: {
  campaignId: number
  disabled: boolean
  onPaid?: () => Promise<void>
}) {
  const [catalog, setCatalog] = useState<MascotCoinCatalog | null>(null)
  const [purchase, setPurchase] = useState<MascotCoinPurchase | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [qr, setQr] = useState('')
  const [copied, setCopied] = useState(false)
  const [loadAttempt, setLoadAttempt] = useState(0)
  const active = useRef(false)
  const locked = useRef(false)
  const intent = useRef<{ packageId: string; requestId: string } | null>(null)
  const notified = useRef<string | null>(null)
  const lastPurchase = useRef<MascotCoinPurchase | null>(null)
  const onPaidRef = useRef(onPaid)
  useEffect(() => {
    onPaidRef.current = onPaid
  }, [onPaid])

  const accept = useCallback((next: MascotCoinPurchase | null) => {
    if (
      next?.id === lastPurchase.current?.id &&
      lastPurchase.current?.status === 'paid' &&
      next?.status !== 'paid'
    )
      return
    lastPurchase.current = next
    setPurchase(next)
    if (next?.status === 'paid' && next.credited_at && notified.current !== next.id) {
      notified.current = next.id
      void onPaidRef.current?.()
    }
  }, [])

  useEffect(() => {
    active.current = true
    const controller = new AbortController()
    setLoading(true)
    setError('')
    void fetchMascotCoinCatalog(campaignId, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return
        setCatalog(data)
        accept(data.purchase)
      })
      .catch((err: unknown) => {
        if (!controller.signal.aborted) setError(message(err))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => {
      active.current = false
      controller.abort()
    }
  }, [campaignId, accept, loadAttempt])

  useEffect(() => {
    setQr('')
    setCopied(false)
    if (!purchase?.pix_key || purchase.status !== 'pending') return
    let cancelled = false
    void QRCode.toDataURL(purchase.pix_key, { width: 240, margin: 2 })
      .then((url) => {
        if (!cancelled) setQr(url)
      })
      .catch(() => {
        /* The copy-and-paste code remains available if QR rendering fails. */
      })
    return () => {
      cancelled = true
    }
  }, [purchase?.pix_key, purchase?.status])

  // Poll only the visible purchase screen, without overlapping requests, for at most five minutes.
  const purchaseId = purchase?.id
  const purchaseStatus = purchase?.status
  useEffect(() => {
    if (
      !purchaseId ||
      !purchaseStatus ||
      !['creating', 'pending', 'creation_uncertain'].includes(purchaseStatus)
    )
      return
    const controller = new AbortController()
    const started = Date.now()
    let timer: number | undefined
    const id = purchaseId
    async function poll() {
      if (controller.signal.aborted || Date.now() - started > 300_000) return
      if (document.visibilityState !== 'hidden') {
        try {
          const next = await fetchMascotCoinPurchase(campaignId, id, controller.signal)
          if (controller.signal.aborted) return
          accept(next)
          if (['paid', 'expired'].includes(next.status)) return
        } catch {
          /* Manual refresh remains available; do not hide the existing PIX. */
        }
      }
      if (!controller.signal.aborted) timer = window.setTimeout(() => void poll(), 5000)
    }
    timer = window.setTimeout(() => void poll(), 5000)
    return () => {
      controller.abort()
      window.clearTimeout(timer)
    }
  }, [campaignId, purchaseId, purchaseStatus, accept])

  async function buy(packageId: string) {
    if (locked.current || disabled || !catalog?.sales_open) return
    locked.current = true
    setBusy(true)
    setError('')
    intent.current ??= { packageId, requestId: crypto.randomUUID() }
    try {
      const next = await createMascotCoinPurchase(
        campaignId,
        intent.current.packageId,
        intent.current.requestId
      )
      if (!active.current) return
      accept(next)
      intent.current = null
    } catch (err) {
      if (!active.current) return
      setError(message(err))
      if (err instanceof ApiClientError && err.status >= 400 && err.status < 500)
        intent.current = null
    } finally {
      locked.current = false
      if (active.current) setBusy(false)
    }
  }

  async function refresh() {
    if (!purchase || locked.current) return
    locked.current = true
    setBusy(true)
    setError('')
    try {
      const next = await fetchMascotCoinPurchase(campaignId, purchase.id)
      if (active.current) accept(next)
    } catch (err) {
      if (active.current) setError(message(err))
    } finally {
      locked.current = false
      if (active.current) setBusy(false)
    }
  }

  const hasOpenPurchase =
    purchase && ['pending', 'creating', 'creation_uncertain'].includes(purchase.status)
  return (
    <div className={coinStyles.root}>
      <div className={coinStyles.intro}>
        <h3>Comprar moedas</h3>
        <p>
          Escolha seu pacote e pague com Pix. As moedas entram no saldo do mascote após a
          confirmação do pagamento.
        </p>
      </div>
      {loading && <p role="status">Carregando pacotes e compras…</p>}
      {error && (
        <p role="alert" className={coinStyles.notice}>
          {error}
        </p>
      )}
      {!catalog && !loading && (
        <button
          className={styles.secondaryButton}
          onClick={() => setLoadAttempt((value) => value + 1)}
        >
          Tentar novamente
        </button>
      )}
      {catalog && !loading && (
        <>
          {!catalog.sales_open && (
            <p className={coinStyles.notice}>
              A compra de moedas está indisponível nesta campanha. Você pode acompanhar um
              pagamento já iniciado.
            </p>
          )}
          {!hasOpenPurchase && (
            <div className={styles.shopGrid}>
              {catalog.packages.map((pack) => (
                <article
                  className={`${styles.shopItem} ${coinStyles.package}`}
                  key={pack.id}
                >
                  <MascotCoin className={coinStyles.coin} />
                  <h3>{pack.coins.toLocaleString('pt-BR')} moedas</h3>
                  <p className={coinStyles.price}>{formatPrice(pack.amount_cents)}</p>
                  <button
                    className={styles.primaryButton}
                    disabled={disabled || busy || !catalog.sales_open || !!intent.current}
                    onClick={() => void buy(pack.id)}
                  >
                    <Icon icon="mdi:qrcode" />
                    {busy
                      ? 'Preparando Pix…'
                      : `Comprar ${pack.coins.toLocaleString('pt-BR')} moedas`}
                  </button>
                </article>
              ))}
            </div>
          )}
          {intent.current && !busy && (
            <button
              className={styles.secondaryButton}
              onClick={() => void buy(intent.current!.packageId)}
            >
              Retomar a mesma compra
            </button>
          )}
          {purchase && (
            <section className={coinStyles.payment} aria-label="Pagamento de moedas">
              <h3>
                {purchase.coins.toLocaleString('pt-BR')} moedas ·{' '}
                {formatPrice(purchase.amount_cents)}
              </h3>
              {purchase.status === 'paid' ? (
                <>
                  <p role="status" className={coinStyles.success}>
                    Pagamento confirmado! {purchase.coins.toLocaleString('pt-BR')} moedas
                    foram adicionadas ao seu mascote.
                  </p>
                  <button
                    className={styles.secondaryButton}
                    onClick={() => void onPaidRef.current?.()}
                  >
                    Atualizar saldo
                  </button>
                </>
              ) : purchase.status === 'expired' ? (
                <p role="status">
                  O prazo para exibir este Pix terminou. Se você já pagou, consulte a
                  confirmação antes de iniciar outra compra.
                </p>
              ) : purchase.status === 'creation_uncertain' ? (
                <p role="status">
                  A criação do Pix precisa de confirmação. Acompanhe este pedido; não
                  repita o pagamento. Se persistir, informe o código abaixo ao suporte.
                </p>
              ) : purchase.status === 'creating' ? (
                <p role="status">Preparando seu Pix…</p>
              ) : (
                <>
                  <p role="status">
                    Aguardando pagamento. As moedas serão creditadas automaticamente.
                  </p>
                  {qr && (
                    <img
                      className={coinStyles.qr}
                      src={qr}
                      alt="QR Code Pix para comprar moedas"
                    />
                  )}
                  {purchase.pix_key && (
                    <>
                      <label className={coinStyles.codeLabel}>
                        Código Pix copia e cola
                        <textarea readOnly value={purchase.pix_key} />
                      </label>
                      <button
                        className={styles.primaryButton}
                        onClick={() => {
                          if (!navigator.clipboard) {
                            setError('Selecione e copie o código Pix acima.')
                            return
                          }
                          void navigator.clipboard
                            .writeText(purchase.pix_key!)
                            .then(() => setCopied(true))
                            .catch(() =>
                              setError('Selecione e copie o código Pix acima.')
                            )
                        }}
                      >
                        {copied ? 'Código copiado!' : 'Copiar código Pix'}
                      </button>
                    </>
                  )}
                  {purchase.expires_at && (
                    <p className={styles.caption}>
                      Disponível até{' '}
                      {new Date(purchase.expires_at).toLocaleTimeString('pt-BR', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                      .
                    </p>
                  )}
                </>
              )}
              {purchase.status !== 'paid' && (
                <button
                  className={styles.secondaryButton}
                  disabled={busy}
                  onClick={() => void refresh()}
                >
                  {busy ? 'Consultando…' : 'Consultar pagamento'}
                </button>
              )}
              <small className={coinStyles.reference}>Pedido {purchase.id}</small>
            </section>
          )}
          <p className={styles.caption}>
            O saldo pertence ao seu mascote e pode ser usado em todas as campanhas.
            Comprar moedas não gera números da sorte ou giros de roleta.
          </p>
        </>
      )}
    </div>
  )
}
